import { useEffect, useState } from 'react'
import classes from '../styles.module.scss'
import { useShared } from '../../../../contexts/shared.context';
import { ISWOT } from '../../../../types/screening.types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useKato } from '../../../../contexts/kato.context';
import { updateScreening } from '../../../../requests/screening.request';
import FooterComponent from '../../components/footer.component';
import Field from '../../../../components/field/field.component';
import { isEmpty, t } from '../../../../helpers/utils';
import { toast } from 'react-toastify';
import { PlusIcon } from '../../../../assets/icons';

const baseClass = classes['screening-tab__swot-analysis-tab'];
const translationKey = 'screening.swot-analysis';
const keyOrder: Array<keyof ISWOT> = ['strengths', 'weaknesses', 'opportunities', 'threats'];

const defaultSwot: ISWOT = {
  strengths: [],
  weaknesses: [],
  opportunities: [],
  threats: [],
};

const SwotAnalysisTab = () => {
  const { selectedInfoYear } = useShared();
  const { kato } = useParams<{ kato: string }>();
  const { i18n: { language } } = useTranslation();
  const { screeningInfo: { swotAnalysis }, updateScreeningInfo } = useKato();
  const [formData, setFormData] = useState<ISWOT>(defaultSwot);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const [addingType, setAddingType] = useState<keyof ISWOT | null>(null);
  const [newField, setNewField] = useState<{ textRu: string; textKz: string } | null>(null);

  const handleSave = () => {
    if (validateFields() && kato) {
      updateScreening(+kato, 'swotAnalysis', formData, selectedInfoYear)
        .then(() => {
          updateScreeningInfo();
          toast.success(t(`admin.success`, language));
        })
        .catch((error) => {
          console.error('Error updating agriculture:', error);
        });
    }
  };

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartAdd = (type: keyof ISWOT) => {
    setNewField({ textRu: '', textKz: '' });
    setAddingType(type);
  };

  const handleCancelAdd = () => {
    setNewField(null);
    setAddingType(null);
  };

  const handleSaveNewField = () => {
    if (!addingType || !validateNewField() || !Array.isArray(formData[addingType])) return;

    setFormData({
      ...formData,
      [addingType]: [
        ...(formData[addingType] ?? []),
        { ...newField }
      ]
    });
    setNewField(null);
    setAddingType(null);
  };


  const validateNewField = () => {
    const newErrors = {} as Record<string, boolean>;
    if (!newField) return false;
    Object.keys(newField).forEach(field => {
      if (isEmpty(newField[field as keyof typeof newField])) {
        newErrors[`new-field-${field}`] = true;
      }
    });
    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  };

  const handleFieldChange = (type: keyof ISWOT, index: number, value: string, field: string) => {
    setFormData({
      ...formData,
      [type]: formData[type]?.map((item, idx) => idx === index ? { ...item, [field]: value } : item)
    });
  };

  useEffect(() => {
    if (swotAnalysis) {
      setFormData(swotAnalysis || defaultSwot);
    } else {
      setFormData(defaultSwot);
    }
  }, [swotAnalysis]);

  const renderFields = (lang: 'ru' | 'kz') => {
    const suffix = lang === 'ru' ? 'Ru' : 'Kz';
    return (
      <div className={classes['fields-container']}>
        {keyOrder.map((key) => {
          return (
            <div className={classes['field-block']} key={key}>
              <div className={classes['field-block__title']}>{t(`${translationKey}.${key}`, lang)}</div>
              <div className={classes['field-block__fields']}>
                {formData[key]?.map((item, index) => {
                  const val = item[`text${suffix}`]?.toString() || '';
                  return (
                    <Field
                      key={`${key}-${index}`}
                      value={val}
                      onChange={(value) => handleFieldChange(key, index, value.toString(), `text${suffix}`)}
                      label=""
                    />
                  );
                })}

                {addingType === key && newField && (
                  <div className={classes['field-block__fields']}>
                    <Field
                      key={`new-field-text${suffix}`}
                      value={newField[`text${suffix}`]}
                      onChange={(value) => setNewField({ ...newField, [`text${suffix}`]: value })}
                      error={errors[`new-field-text${suffix}`]}
                      label=""
                      required
                    />
                  </div>
                )}
              </div>

              <div className={classes['field-block__actions']}>
                {addingType === key ? (
                  <>
                    <button onClick={handleSaveNewField}>{t('actions.save', lang)}</button>
                    <button onClick={handleCancelAdd}>{t('actions.cancel', lang)}</button>
                  </>
                ) : (
                  <button onClick={() => handleStartAdd(key)}><PlusIcon /> {t('actions.add', lang)}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`grid`]}>
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>

      <FooterComponent
        onSave={handleSave}
        validateFields={validateFields}
      />
    </div>
  )
}

export default SwotAnalysisTab